// Global Types
import type {
  $RedirectParams,
  $RedirectResponse,
} from '@vroskus/library-navigation';
import type {
  $FullRegion,
  $Model,
} from 'common.data';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  getLocation,
} from '../../../helpers';

import * as mainActionTypes from './types';

// Types
import type {
  $Services,
} from '../../../services';
import type {
  $Action,
  $ActionResult,
  $Dispatch,
  $State,
} from '../..';

// redirect action
type $RedirectActionParams = $RedirectParams;
type $RedirectActionResponse = $RedirectResponse;
export type $RedirectAction = (
  arg0: $RedirectActionParams,
) => $ActionResult<$RedirectActionResponse>;

export const redirect: $Action<
$RedirectActionResponse,
$RedirectActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => services.Navigation.redirect(params),
  type: mainActionTypes.REDIRECT,
});

// getRegions action
type $GetRegionsActionParams = void;
type $GetRegionsActionResponse = Array<$FullRegion>;
export type $GetRegionsAction = (
  arg0: $GetRegionsActionParams,
) => $ActionResult<$GetRegionsActionResponse>;

export const getRegions: $Action<
$GetRegionsActionResponse,
$GetRegionsActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => services.Api.getRegions(),
  type: mainActionTypes.GET_REGIONS,
});

// setRegion action
type $SetRegionActionParams = {
  RegionId: string;
};
type $SetRegionActionResponse = {
  RegionId: string;
};
export type $SetRegionAction = (
  arg0: $SetRegionActionParams,
) => $ActionResult<$SetRegionActionResponse>;

export const setRegion: $Action<
$SetRegionActionResponse,
$SetRegionActionParams
> = ({
  RegionId,
}) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    services.LocalStorage.setItem(
      'RegionId',
      RegionId,
    );

    return {
      RegionId,
    };
  },
  type: mainActionTypes.SET_REGION,
});

// setRegionFromStorage action
type $SetRegionFromStorageActionParams = void;
type $SetRegionFromStorageActionResponse = {
  RegionId: null | string;
};
export type $SetRegionFromStorageAction = (
  arg0: $SetRegionFromStorageActionParams,
) => $ActionResult<$SetRegionFromStorageActionResponse>;

export const setRegionFromStorage: $Action<
$SetRegionFromStorageActionResponse,
$SetRegionFromStorageActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    let RegionId: null | string = null;

    const localStorageRegionId = services.LocalStorage.getItem('RegionId');

    if (localStorageRegionId) {
      RegionId = localStorageRegionId;
    }

    return {
      RegionId,
    };
  },
  type: mainActionTypes.SET_REGION_FROM_STORAGE,
});

// setRegionFromHost action
type $SetRegionFromHostActionParams = void;
type $SetRegionFromHostActionResponse = {
  RegionId: null | string;
};
export type $SetRegionFromHostAction = (
  arg0: $SetRegionFromHostActionParams,
) => $ActionResult<$SetRegionFromHostActionResponse>;

export const setRegionFromHost: $Action<
$SetRegionFromHostActionResponse,
$SetRegionFromHostActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
) => dispatch({
  payload: async () => {
    const state = getState();

    const {
      regions,
    } = state.main;

    let RegionId: null | string = null;

    const {
      subDomain,
    } = getLocation();

    const foundRegion: $FullRegion | undefined = _.find(
      regions,
      {
        Domain: subDomain,
      },
    );

    if (foundRegion) {
      RegionId = foundRegion.id;
    }

    return {
      RegionId,
    };
  },
  type: mainActionTypes.SET_REGION_FROM_HOST,
});

// setupRegion action
type $SetupRegionActionParams = void;
type $SetupRegionActionResponse = void;
export type $SetupRegionAction = (
  arg0: $SetupRegionActionParams,
) => $ActionResult<$SetupRegionActionResponse>;

export const setupRegion: $Action<
$SetupRegionActionResponse,
$SetupRegionActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    await setRegionFromHost()(
      dispatch,
      getState,
      services,
    );

    const state = getState();

    const {
      RegionId,
    } = state.main;

    if (RegionId !== null) {
      await setRegion({
        RegionId,
      })(
        dispatch,
        getState,
        services,
      );
    } else {
      await setRegionFromStorage()(
        dispatch,
        getState,
        services,
      );
    }
  },
  type: mainActionTypes.SETUP_REGION,
});

// setLanguage action
type $SetLanguageActionParams = {
  language: string;
};
type $SetLanguageActionResponse = {
  language: string;
};
export type $SetLanguageAction = (
  arg0: $SetLanguageActionParams,
) => $ActionResult<$SetLanguageActionResponse>;

export const setLanguage: $Action<
$SetLanguageActionResponse,
$SetLanguageActionParams
> = ({
  language,
}) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    services.Cookies.setItem(
      'language',
      language,
    );

    i18next.changeLanguage(language);
    moment.locale(language);

    return {
      language,
    };
  },
  type: mainActionTypes.SET_LANGUAGE,
});

// setupLanguage action
type $SetupLanguageActionParams = void;
type $SetupLanguageActionResponse = void;
export type $SetupLanguageAction = (
  arg0: $SetupLanguageActionParams,
) => $ActionResult<$SetupLanguageActionResponse>;

export const setupLanguage: $Action<
$SetupLanguageActionResponse,
$SetupLanguageActionParams
> = () => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    let language = services.Cookies.getItem('language');

    if (language) {
      i18next.changeLanguage(language);
    } else {
      language = i18next.language;
    }

    await setLanguage({
      language,
    })(
      dispatch,
      getState,
      services,
    );
  },
  type: mainActionTypes.SETUP_LANGUAGE,
});

// setupUser action
type $SetupUserActionParams = $Model<'Crewmember'>;
type $SetupUserActionResponse = void;
export type $SetupUserAction = (
  arg0: $SetupUserActionParams,
) => $ActionResult<$SetupUserActionResponse>;

export const setupUser: $Action<
$SetupUserActionResponse,
$SetupUserActionParams
> = (params) => (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => dispatch({
  payload: async () => {
    ErrorLog.setUser({
      email: params.Email,
      id: params.id,
      username: params.Name,
    });

    services.Mixpanel.setUser({
      $email: params.Email,
      $name: params.Name,
      id: params.id,
    });
  },
  type: mainActionTypes.SETUP_USER,
});
